import { FIREBASE_AUTH } from './firebase-auth.token';
import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap, BehaviorSubject, from, switchMap, catchError, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { FirebaseError } from 'firebase/app';
import { Auth, AuthProvider, GoogleAuthProvider, FacebookAuthProvider, OAuthProvider, OAuthCredential, UserCredential } from 'firebase/auth';
import { FirebaseSocialAuthGateway } from './firebase-social-auth.gateway';
import { AuthStore, FlowContextStore } from '../stores';
import {
  Customer,
  RegisterRequest,
  LoginRequest,
  AuthResponse,
  CustomerTenant,
  SwitchTenantRequest,
  UpdateCustomerProfileRequest
} from '../models';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private auth = inject(FIREBASE_AUTH);
  private socialAuth = inject(FirebaseSocialAuthGateway);
  // OAuth credentials stay only in memory while the user proves ownership of the existing account.
  private pendingLink: { credential: OAuthCredential; email: string; createdAt: number } | null = null;
  private pendingReactivation: {result:UserCredential;email:string;createdAt:number}|null=null;
  private emailLinkChallenge: {id:string;clientSecret:string;pending:object} | null = null;
  private verifiedLinkAccount: UserCredential | null = null;
  private authStore = inject(AuthStore);
  private flowContextStore = inject(FlowContextStore);

  private currentCustomerSubject = new BehaviorSubject<Customer | null>(null);
  public currentCustomer$ = this.currentCustomerSubject.asObservable();

  constructor() {
    // Cargar datos del usuario al iniciar
    this.loadCustomerFromStorage();
  }

  private loadCustomerFromStorage() {
    const customer = this.authStore.customer();
    if (customer) {
      this.currentCustomerSubject.next(this.normalizeCustomer(customer));
    }
  }

  register(data: RegisterRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${environment.apiUrl}/api/customers/register`, data)
      .pipe(
        tap(response => this.handleAuthSuccess(response))
      );
  }

  login(credentials: LoginRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${environment.apiUrl}/auth/customer-login`, credentials)
      .pipe(
        tap(response => this.handleAuthSuccess(response))
      );
  }

  listTenants(): Observable<CustomerTenant[]> {
    return this.http.get<CustomerTenant[]>(`${environment.apiUrl}/auth/customer/tenants`);
  }

  switchTenant(request: SwitchTenantRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${environment.apiUrl}/auth/customer/switch-tenant`, request)
      .pipe(
        tap(response => this.handleAuthSuccess(response))
      );
  }

  private handleAuthSuccess(response: AuthResponse) {
    
    if (!response || !response.customer || !response.customer.id) {
      console.error('Invalid auth response: missing customer');
      return;
    }
    
    if (!response.accessToken) {
      console.error('Invalid auth response: missing accessToken');
      return;
    }
    
    const normalizedCustomer = this.normalizeCustomer(response.customer, response);
    this.authStore.setAuth(response.accessToken, normalizedCustomer);
    this.currentCustomerSubject.next(normalizedCustomer);
    if (normalizedCustomer.companyId) {
      this.flowContextStore.setContext({ companyId: normalizedCustomer.companyId });
    }
    
  }

  // Login con Google
  loginWithGoogle(): Observable<AuthResponse> {
    return this.socialLogin(new GoogleAuthProvider());
  }

  // Login con Facebook
  loginWithFacebook(): Observable<AuthResponse> {
    return this.socialLogin(new FacebookAuthProvider());
  }

  // Login con Apple
  loginWithApple(): Observable<AuthResponse> {
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    return this.socialLogin(provider);
  }

  // Login con Microsoft
  loginWithMicrosoft(): Observable<AuthResponse> {
    const provider = new OAuthProvider('microsoft.com');
    provider.setCustomParameters({
      'tenant': 'common'
    });
    return this.socialLogin(provider);
  }

  private socialLogin(provider: AuthProvider): Observable<AuthResponse> {
    return from(this.socialAuth.signIn(provider)).pipe(
      catchError((error: FirebaseError & { customData?: { email?: string } }) => {
        if (error.code === 'auth/account-exists-with-different-credential') {
          this.verifiedLinkAccount = null;
          const credential = this.socialAuth.credentialFromError(error, provider.providerId);
          const email = error.customData?.email;
          this.pendingLink = credential && email
            ? { credential, email: email.toLowerCase(), createdAt: Date.now() } : null;
          return throwError(() => new Error(this.pendingLink
            ? `Este correo ya tiene una cuenta. Para vincular ${provider.providerId === 'facebook.com' ? 'Facebook' : 'este proveedor'}, confirma tu correo con un código de verificación.`
            : 'Este correo ya tiene una cuenta. Entra con el proveedor que utilizaste al registrarte.'));
        }
        if (error.code === 'auth/popup-closed-by-user') {
          return throwError(() => new Error('Has cancelado el inicio de sesión.'));
        }
        return throwError(() => error);
      }),
      switchMap(result => from(this.completePendingLink(result))),
      switchMap(result => this.handleSSOSuccess(result))
    );
  }

  private async completePendingLink(result: UserCredential): Promise<UserCredential> {
    const pending = this.pendingLink;
    if (!pending) return result;
    if (result.providerId === pending.credential.providerId) { this.cancelPendingAccountLink(); return result; }
    if (Date.now() - pending.createdAt > 5 * 60 * 1000) {
      this.cancelPendingAccountLink();
      throw new Error('La solicitud de vinculación ha caducado. Vuelve a entrar con Facebook.');
    }
    if (!result.user.email || result.user.email.toLowerCase() !== pending.email) {
      this.cancelPendingAccountLink();
      throw new Error('El correo de la cuenta elegida no coincide. Vuelve a Facebook y usa la cuenta correcta.');
    }
    this.verifiedLinkAccount = result;
    return result;
  }

  getPendingAccountLink(): { email: string; provider: string } | null {
    const pending = this.pendingLink;
    const reactivation=this.pendingReactivation;
    const action=pending||reactivation;
    if(!action)return null;
    const id=pending?.credential.providerId||reactivation?.result.providerId||'';
    return {email:action.email,provider:({'google.com':'Google','apple.com':'Apple','facebook.com':'Facebook','microsoft.com':'Microsoft'} as Record<string,string>)[id]||'este método'};
  }

  getAccountLoginMethods(accessToken: string): Observable<{methods: {id: string; connected: boolean}[]}> {
    return this.http.get<{methods: {id: string; connected: boolean}[]}>(`${environment.apiUrl}/auth/access`, {headers: {Authorization: `Bearer ${accessToken}`}});
  }

  getVerifiedAccountPhoto(): string | null { return this.verifiedLinkAccount?.user.photoURL || null; }

  requestAccountLinkCode(): Observable<{expiresAt:string;resendAfterSeconds:number}> {
    const pending=this.pendingLink||this.pendingReactivation;
    if(!pending || Date.now()-pending.createdAt>5*60*1000)return throwError(()=>new Error('La solicitud ha caducado. Vuelve a iniciar sesión.'));
    const clientSecret=Array.from(crypto.getRandomValues(new Uint8Array(32)),b=>b.toString(16).padStart(2,'0')).join('');
    return this.http.post<{id:string;expiresAt:string;resendAfterSeconds:number}>(`${environment.apiUrl}/auth/customer-link-email/request`,{email:pending.email,provider:'credential' in pending?pending.credential.providerId:pending.result.providerId,clientSecret}).pipe(
      tap(response=>{if((this.pendingLink||this.pendingReactivation)===pending){pending.createdAt=Date.now();this.emailLinkChallenge={id:response.id,clientSecret,pending};}})
    );
  }

  verifyAccountLinkCode(code:string): Observable<AuthResponse> {
    if(this.pendingReactivation)return this.verifyReactivationCode(code);
    const challenge=this.emailLinkChallenge,pending=this.pendingLink;
    if(!challenge || !pending || challenge.pending!==pending || Date.now()-pending.createdAt>5*60*1000)return throwError(()=>new Error('La solicitud ha caducado. Vuelve a iniciar sesión.'));
    const stillPending=()=>{if(this.pendingLink!==pending)throw new Error('La vinculación se ha cancelado.');};
    return this.http.post<{firebaseCustomToken:string}>(`${environment.apiUrl}/auth/customer-link-email/verify`,{id:challenge.id,clientSecret:challenge.clientSecret,provider:pending.credential.providerId,code}).pipe(
      switchMap(response=>{stillPending();return from(this.socialAuth.signInCustomToken(response.firebaseCustomToken));}),
      switchMap(verified=>{
        stillPending();
        if(verified.user.email?.toLowerCase()!==pending.email)throw new Error('No se pudo confirmar esta cuenta.');
        return from(this.socialAuth.link(verified.user,pending.credential));
      }),
      switchMap(()=>{stillPending();return from(this.socialAuth.signInCredential(pending.credential));}),
      switchMap(result=>{stillPending();this.cancelPendingAccountLink();return this.handleSSOSuccess(result);})
    );
  }

  private verifyReactivationCode(code:string): Observable<AuthResponse> {
    const pending=this.pendingReactivation,challenge=this.emailLinkChallenge;
    if(!pending||!challenge||challenge.pending!==pending||Date.now()-pending.createdAt>5*60*1000)return throwError(()=>new Error('La solicitud ha caducado. Vuelve a iniciar sesión.'));
    return this.http.post(`${environment.apiUrl}/auth/customer-link-email/verify`,{id:challenge.id,clientSecret:challenge.clientSecret,provider:pending.result.providerId,code}).pipe(
      switchMap(()=>{
        if(this.pendingReactivation!==pending)throw new Error('La vinculación se ha cancelado.');
        this.cancelPendingAccountLink();
        return this.handleSSOSuccess(pending.result);
      })
    );
  }

  confirmPendingAccountLink(): Observable<AuthResponse> {
    const pending = this.pendingLink;
    const verified = this.verifiedLinkAccount;
    if (!pending || !verified || Date.now() - pending.createdAt > 5 * 60 * 1000) {
      this.cancelPendingAccountLink();
      return throwError(() => new Error('La solicitud ha caducado. Vuelve a iniciar sesión con Facebook.'));
    }
    this.cancelPendingAccountLink();
    return from(this.socialAuth.link(verified.user, pending.credential)).pipe(
      switchMap(() => from(this.socialAuth.signInCredential(pending.credential))),
      switchMap(result => this.handleSSOSuccess(result))
    );
  }

  cancelPendingAccountLink(): void { this.pendingLink = null; this.verifiedLinkAccount = null; this.emailLinkChallenge = null; this.pendingReactivation=null; }

  private handleSSOSuccess(result: UserCredential): Observable<AuthResponse> {
    // Obtener el token de Firebase
    return from(result.user.getIdToken() as Promise<string>).pipe(
      switchMap((token: string) => {
        // Enviar al backend para crear/actualizar usuario
        const ssoData: any = {
          email: result.user.email,
          displayName: result.user.displayName,
          photoURL: result.user.photoURL,
          provider: result.providerId,
          firebaseUid: result.user.uid,
          firebaseToken: token
        };
        // Añadir companyId del store si existe
        const companyId = this.flowContextStore.companyId();
        if (companyId) {
          ssoData.companyId = companyId;
        }
        const headers = { Authorization: `Bearer ${token}` };
        return this.http.post<AuthResponse>(
          `${environment.apiUrl}/api/customers/sso-login`,
          ssoData,
          { headers }
        ).pipe(
          catchError(err => {
            if(err?.error?.code==='LOGIN_METHOD_RELINK_REQUIRED'&&result.user.email){
              this.cancelPendingAccountLink();
              this.pendingReactivation={result,email:result.user.email.toLowerCase(),createdAt:Date.now()};
              return throwError(()=>new Error('Confirma tu correo para volver a vincular este método.'));
            }
            return this.resetFirebaseSessionAfterBackendFailure(err);
          })
        );
      }),
      tap(response => { if (!this.verifiedLinkAccount) this.handleAuthSuccess(response); })
    );
  }

  logout() {
    this.cancelPendingAccountLink();
    this.authStore.clear();
    this.currentCustomerSubject.next(null);
    void this.auth.signOut().catch(error => console.warn('Firebase signOut failed during logout:', error));
    this.router.navigate(['/login']);
  }

  isAuthenticated(): boolean {
    return this.authStore.isAuthenticated();
  }

  getToken(): string | null {
    return this.authStore.token();
  }

  getCustomer(): Customer | null {
    // Intentar primero del BehaviorSubject
    let customer = this.currentCustomerSubject.value;
    
    // Si no hay en el subject, intentar leer del store
    if (!customer) {
      customer = this.authStore.customer();
      if (customer && customer.id) {
        customer = this.normalizeCustomer(customer);
        this.currentCustomerSubject.next(customer);
      }
    }
    
    return customer;
  }

  getActiveCompanyId(): number | null {
    const customer = this.getCustomer();
    return customer?.activeCompanyId || customer?.companyId || customer?.company?.id || this.flowContextStore.companyId() || null;
  }

  getCustomerProfileId(): number | null {
    return this.getCustomer()?.customerProfileId || null;
  }

  updateCustomer(customer: Customer) {
    const normalizedCustomer = this.normalizeCustomer(customer);
    this.authStore.updateCustomer(normalizedCustomer);
    this.currentCustomerSubject.next(normalizedCustomer);
  }

  updateCustomerProfile(customerProfileId: number, payload: UpdateCustomerProfileRequest): Observable<Customer> {
    return this.http.put<Customer>(`${environment.apiUrl}/api/customers/profiles/${customerProfileId}`, payload)
      .pipe(
        tap(customer => this.updateCustomer(customer))
      );
  }

  getProfilePhoto(): Observable<{photoUrl:string|null}> {
    const owner=this.getCustomer();
    return this.http.get<{photoUrl:string|null}>(`${environment.apiUrl}/api/customers/me/photo`).pipe(tap(photo=>this.storeProfilePhoto(photo.photoUrl,owner)));
  }
  uploadProfilePhoto(file:File): Observable<{photoUrl:string|null}> {
    const owner=this.getCustomer();
    const body=new FormData();body.append('file',file,'profile.jpg');
    return this.http.post<{photoUrl:string|null}>(`${environment.apiUrl}/api/customers/me/photo`,body).pipe(tap(photo=>this.storeProfilePhoto(photo.photoUrl,owner)));
  }
  removeProfilePhoto(): Observable<{photoUrl:string|null}> {
    const owner=this.getCustomer();
    return this.http.delete<{photoUrl:string|null}>(`${environment.apiUrl}/api/customers/me/photo`).pipe(tap(photo=>this.storeProfilePhoto(photo.photoUrl,owner)));
  }
  private storeProfilePhoto(photoUrl:string|null,owner:Customer|null){const customer=this.getCustomer();if(customer&&owner&&(customer.identityId??customer.id)===(owner.identityId??owner.id)&&customer.email===owner.email)this.updateCustomer({...customer,photoUrl});}

  private resetFirebaseSessionAfterBackendFailure(err: any): Observable<never> {
    this.cancelPendingAccountLink();
    const normalizedError = this.toUserFacingAuthError(err);
    return from(this.auth.signOut()).pipe(
      catchError(() => from(Promise.resolve())),
      switchMap(() => throwError(() => normalizedError))
    );
  }

  private toUserFacingAuthError(err: any): Error {
    const body = err?.error;
    let message = (typeof body === 'string' ? body : body?.message || body?.detail) || err?.message || 'No se pudo completar el inicio de sesion social.';
    if (body?.code === 'VALIDATION_ERROR') {
      const fields = Object.keys(body.details || {});
      message = fields.includes('email')
        ? 'La cuenta seleccionada no ha facilitado un correo válido. Prueba con otra cuenta o accede con email.'
        : 'No se pudo validar el acceso con esa cuenta. Vuelve a seleccionarla o accede con email.';
    }
    return new Error(message);
  }

  private normalizeCustomer(customer: Customer, response?: AuthResponse): Customer {
    const resolvedCompanyId = response?.activeCompanyId ?? customer.activeCompanyId ?? customer.company?.id ?? customer.companyId;
    const resolvedCompanyName = customer.company?.name ?? customer.companyName;

    return {
      ...customer,
      acceptsNotifications: customer.marketingOptIn ?? customer.acceptsNotifications ?? false,
      acceptsTerms: customer.dataProcessingConsent ?? customer.acceptsTerms ?? false,
      identityId: response?.identityId ?? customer.identityId,
      activeCompanyId: resolvedCompanyId,
      customerProfileId: response?.customerProfileId ?? customer.customerProfileId,
      phone: customer.phone ?? customer.phoneNumber,
      phoneNumber: customer.phoneNumber ?? customer.phone,
      companyId: resolvedCompanyId,
      companyName: resolvedCompanyName,
      company: resolvedCompanyId
        ? {
            id: resolvedCompanyId,
            name: resolvedCompanyName || 'Empresa'
          }
        : customer.company
    };
  }
}
